import * as LucideIcons from 'lucide-react';
import { Sparkles } from 'lucide-react';

/** A homepage_sections row as returned by GET /homepage. */
export interface HomepageSection {
  id: string;
  section_type: string;
  title?: string | null;
  subtitle?: string | null;
  content?: any;
  is_active?: boolean;
}

/** The wizard's per-tenant pricing tier shape (GeneratedPricingTier). */
export interface PricingTier {
  id?: string;
  name: string;
  /** Number (0 = free) from the generator, or a label like "Custom"/"Contact us" set in the admin builder. */
  price: number | string;
  interval?: 'month' | 'year' | string;
  description?: string;
  /**
   * Short callout rendered directly under the price — e.g. a partner product's
   * "Rumio member rate: $29/mo" line. Copy lives in the DB row, not here.
   */
  note?: string;
  features?: string[];
  cta_text?: string;
  cta_link?: string;
  featured?: boolean;
  /**
   * `subscription_plans.slug` this tier sells. Set it and the CTA becomes a
   * real checkout link (signup → Stripe) instead of a generic /signup bounce;
   * leave it unset and the tier stays pure marketing (e.g. "Contact us").
   */
  plan_slug?: string;
}

export function findSection(
  sections: HomepageSection[] | undefined,
  type: string,
): HomepageSection | undefined {
  return (sections ?? []).find((s) => s.section_type === type && s.is_active !== false);
}

export interface CtaBanner {
  heading?: string;
  subheading?: string;
  ctaText?: string;
  ctaLink?: string;
}

/** Pull the tenant's closing-CTA copy from the cta_banner homepage section. */
export function getCtaBanner(sections: HomepageSection[] | undefined): CtaBanner {
  const cta = findSection(sections, 'cta_banner');
  if (!cta) return {};
  return {
    heading: cta.title ? String(cta.title) : undefined,
    subheading: cta.subtitle ? String(cta.subtitle) : undefined,
    ctaText: cta.content?.cta_text ? String(cta.content.cta_text) : undefined,
    ctaLink: cta.content?.cta_link ? String(cta.content.cta_link) : undefined,
  };
}

/** Resolve a lucide icon by name, falling back to Sparkles. */
export function resolveIcon(name?: string): React.ComponentType<{ className?: string }> {
  const Icon = name ? ((LucideIcons as any)[name] as React.ComponentType<{ className?: string }> | undefined) : undefined;
  return Icon ?? Sparkles;
}
